import { useState, useEffect, useRef } from "react";
import api from "../../api/client";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../hooks/use-toast";
import OdysboardView from "../shared/OdysboardView";
import ParcoursView from "../../components/ParcoursView";
import CoachingSessionsView from "../shared/CoachingSessionsView";
import BadgesView from "../../components/BadgesView";
import InvestmentObjectiveCard from "../../components/InvestmentObjectiveCard";
import NotificationPreferences from "../../components/NotificationPreferences";
import AgendaView from "../../components/AgendaView";
import NextAppointmentBanner from "../../components/NextAppointmentBanner";

const StatusBadge = ({ status }) => {
  const map = { pending: "badge-pending", in_progress: "badge-in_progress", completed: "badge-completed" };
  const labels = { pending: "En attente", in_progress: "En cours", completed: "Terminé" };
  return <span className={`px-2.5 py-1 rounded-lg text-xs font-medium ${map[status] || "bg-slate-700 text-slate-300"}`}>{labels[status] || status}</span>;
};

// ─── Photo Gallery Component ──────────────────────────────────────────────────
function PhotoGallery({ visitId, photos = [], onUpdate }) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const { toast } = useToast();

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    
    setUploading(true);
    const formData = new FormData();
    Array.from(files).forEach(f => formData.append("files", f));
    
    try {
      await api.post(`/visits/${visitId}/photos`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast({ title: "Photos ajoutées" });
      onUpdate?.();
    } catch (err) {
      toast({ variant: "destructive", title: "Erreur", description: err.response?.data?.detail || "Upload échoué" });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm("Supprimer cette photo ?")) return;
    try {
      await api.delete(`/visits/${visitId}/photos/${filename}`);
      toast({ title: "Photo supprimée" });
      onUpdate?.();
    } catch (err) {
      toast({ variant: "destructive", title: "Erreur" });
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-slate-700/30">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-slate-500">{photos.length}/10 photos</span>
        {photos.length < 10 && (
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            {uploading ? "Upload..." : "Ajouter"}
          </button>
        )}
        <input ref={fileInputRef} type="file" multiple accept="image/*" className="hidden" onChange={handleUpload} />
      </div>
      {photos.length > 0 && (
        <div className="photo-grid">
          {photos.map((p, i) => (
            <div key={i} className="photo-item group relative">
              <img src={`${process.env.REACT_APP_BACKEND_URL}${p.url}`} alt="" />
              <button
                onClick={() => handleDelete(p.filename)}
                className="absolute top-1 right-1 w-6 h-6 bg-red-500/80 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Timeline Component ───────────────────────────────────────────────────────
function ProgressTimeline({ steps }) {
  const completed = steps.filter(s => s.status === "completed").length;
  const total = steps.length;
  
  return (
    <div className="glass-card rounded-2xl p-6">
      <h4 className="text-sm font-semibold text-gray-900 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>Timeline de progression</h4>
      <div className="relative">
        {/* Progress line */}
        <div className="absolute left-[18px] top-0 bottom-0 w-0.5 bg-gray-200" />
        <div className="absolute left-[18px] top-0 w-0.5 bg-gradient-to-b from-[#1a2744] to-emerald-500 transition-all duration-700" 
          style={{ height: `${total > 0 ? (completed / total) * 100 : 0}%` }} />
        
        <div className="space-y-4">
          {steps.map((step, i) => (
            <div key={step.id} className="flex items-start gap-4 relative">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 z-10 transition-all ${
                step.status === "completed" ? "bg-emerald-500 timeline-dot-completed" :
                step.status === "in_progress" ? "bg-[#d4a012] timeline-dot-active" :
                "bg-white border-2 border-gray-300"
              }`}>
                {step.status === "completed" ? (
                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <span className={`text-xs font-bold ${step.status === "in_progress" ? "text-white" : "text-gray-500"}`}>{i + 1}</span>
                )}
              </div>
              <div className="flex-1 pb-4">
                <div className="flex items-center gap-2 mb-1">
                  <p className={`text-sm font-medium ${step.status === "completed" ? "text-emerald-600" : "text-gray-900"}`}>{step.title}</p>
                  <StatusBadge status={step.status} />
                </div>
                {step.description && <p className="text-xs text-gray-500">{step.description}</p>}
                {step.deadline && <p className="text-xs text-gray-400 mt-1">Échéance: {new Date(step.deadline).toLocaleDateString("fr-FR")}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function StudentDashboard() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [progress, setProgress] = useState(null);
  const [notes, setNotes] = useState("");
  const [notesEditing, setNotesEditing] = useState(false);
  const [visits, setVisits] = useState([]);
  const [cahier, setCahier] = useState({});
  const [showVisitModal, setShowVisitModal] = useState(false);
  const [visitForm, setVisitForm] = useState({ address: "", visit_date: "", price: "", link: "", comments: "" });
  const [cahierEditing, setCahierEditing] = useState(false);
  const [cahierForm, setCahierForm] = useState({});
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summary, setSummary] = useState("");
  const [coachSettings, setCoachSettings] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [progressRes, notesRes, visitsRes, cahierRes] = await Promise.all([
        api.get("/student/progress"),
        api.get("/notes"),
        api.get("/visits"),
        api.get("/cahier"),
      ]);
      setProgress(progressRes.data);
      setNotes(notesRes.data.content || "");
      setVisits(visitsRes.data);
      setCahier(cahierRes.data);
      setCahierForm(cahierRes.data);
      
      // Get coach settings for Calendly
      if (progressRes.data.coach_id) {
        try {
          const coachRes = await api.get(`/users/${progressRes.data.coach_id}/settings`);
          setCoachSettings(coachRes.data);
        } catch (e) {
          console.warn("Could not load coach settings:", e.message);
        }
      }
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur de chargement" });
    }
  };

  const handleSaveNotes = async () => {
    try {
      await api.put("/notes", { content: notes });
      setNotesEditing(false);
      toast({ title: "Notes sauvegardées" });
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur" });
    }
  };

  const handleUpdateStepStatus = async (stepId, status) => {
    try {
      await api.put(`/steps/${stepId}`, { status });
      const res = await api.get("/student/progress");
      setProgress(res.data);
      toast({ title: "Statut mis à jour" });
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur" });
    }
  };

  const handleCreateVisit = async () => {
    if (!visitForm.address.trim() || !visitForm.visit_date) return;
    try {
      await api.post("/visits", { ...visitForm, price: visitForm.price ? parseFloat(visitForm.price) : null });
      setShowVisitModal(false);
      setVisitForm({ address: "", visit_date: "", price: "", link: "", comments: "" });
      const res = await api.get("/visits");
      setVisits(res.data);
      toast({ title: "Visite ajoutée" });
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur", description: e.response?.data?.detail || "Erreur" });
    }
  };

  const handleDeleteVisit = async (id) => {
    if (!window.confirm("Supprimer cette visite ?")) return;
    try {
      await api.delete(`/visits/${id}`);
      setVisits(visits.filter(v => v.id !== id));
      toast({ title: "Visite supprimée" });
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur" });
    }
  };

  const handleSaveCahier = async () => {
    try {
      const payload = { ...cahierForm };
      if (payload.budget_total) payload.budget_total = parseFloat(payload.budget_total);
      if (payload.surface) payload.surface = parseFloat(payload.surface);
      if (payload.nb_pieces) payload.nb_pieces = parseInt(payload.nb_pieces);
      if (payload.rendement) payload.rendement = parseFloat(payload.rendement);
      const res = await api.put("/cahier", payload);
      // Mettre à jour l'état local avec les données sauvegardées
      setCahier(res.data);
      setCahierForm(res.data);
      setCahierEditing(false);
      toast({ title: "Cahier des charges sauvegardé" });
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur" });
    }
  };

  const handleGenerateSummary = async () => {
    setSummaryLoading(true);
    try {
      const res = await api.post("/ai/summary", {});
      setSummary(res.data.summary);
      toast({ title: "Résumé généré" });
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur IA" });
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await api.get(`/export/pdf/${user.id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `progression_${user.first_name}_${user.last_name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast({ title: "PDF téléchargé" });
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur export PDF" });
    }
  };

  const tabs = [
    { key: "dashboard", label: "Tableau de bord", icon: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" },
    { key: "parcours", label: "Mon parcours", icon: "M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" },
    { key: "badges", label: "Mes badges", icon: "M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" },
    { key: "sessions", label: "Mes sessions", icon: "M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" },
    { key: "visits", label: "Mes visites", icon: "M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" },
    { key: "cahier", label: "Cahier des charges", icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" },
    { key: "notes", label: "Mes notes", icon: "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" },
    { key: "odysboard", label: "Odysboard", icon: "M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" },
    { key: "notifications", label: "Notifications", icon: "M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1h6z" },
    { key: "agenda", label: "Mon agenda", icon: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" },
  ];

  const pct = progress?.progress || 0;

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Tabs - Mobile optimized: icons only on small screens */}
      <div className="flex gap-2 p-1.5 bg-white rounded-xl border border-gray-200 shadow-sm overflow-x-auto scrollbar-hide mb-2">
        {tabs.map(t => (
          <button key={t.key} data-testid={`student-tab-${t.key}`} onClick={() => setActiveTab(t.key)}
            className={`flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap flex-1 sm:flex-initial min-w-[44px] ${
              activeTab === t.key 
                ? "bg-[#1a2744] text-white shadow-sm" 
                : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
            }`}>
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={t.icon} />
            </svg>
            <span className="hidden sm:inline">{t.label}</span>
          </button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {activeTab === "dashboard" && (
        <div className="space-y-4 sm:space-y-6 animate-fade-in">
          {/* Progress Hero - Mobile optimized */}
          <div className="glass-card rounded-xl sm:rounded-2xl p-4 sm:p-6 overflow-hidden relative">
            <div className="absolute inset-0 bg-gradient-to-r from-[#1a2744]/5 to-[#d4a012]/5" />
            <div className="relative z-10">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4 sm:mb-6">
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900" style={{ fontFamily: "Outfit, sans-serif" }}>Bonjour, {user?.first_name} !</h3>
                  <p className="text-gray-500 text-xs sm:text-sm mt-1">Votre progression globale</p>
                </div>
                <div className="text-left sm:text-right">
                  <span className="text-4xl sm:text-5xl font-bold text-[#1a2744]" style={{ fontFamily: "Outfit, sans-serif" }}>{pct}%</span>
                </div>
              </div>
              <div className="progress-track">
                <div className="progress-bar" style={{ width: `${pct}%` }} />
              </div>
              <div className="flex flex-wrap gap-3 sm:gap-6 mt-4">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 sm:w-3 h-2.5 sm:h-3 rounded-full bg-emerald-500" />
                  <span className="text-xs sm:text-sm text-gray-600">{progress?.completed_steps || 0} complétées</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2.5 sm:w-3 h-2.5 sm:h-3 rounded-full bg-cyan-500" />
                  <span className="text-xs sm:text-sm text-gray-600">{progress?.in_progress_steps || 0} en cours</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2.5 sm:w-3 h-2.5 sm:h-3 rounded-full bg-gray-400" />
                  <span className="text-xs sm:text-sm text-gray-600">{progress?.pending_steps || 0} en attente</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-amber-500" />
                  <span className="text-sm text-gray-600">{progress?.visits_count || 0} visites</span>
                </div>
              </div>
            </div>
          </div>

          <NextAppointmentBanner />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Timeline */}
            {progress?.steps?.length > 0 && <ProgressTimeline steps={progress.steps} />}

            {/* Quick Actions & Summary */}
            <div className="space-y-6">
              {/* Quick Actions */}
              <div className="glass-card rounded-2xl p-6">
                <h4 className="text-sm font-semibold text-gray-900 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>Actions rapides</h4>
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={handleExportPDF} data-testid="export-pdf-btn" className="btn-secondary flex items-center justify-center gap-2 py-3 text-sm">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export PDF
                  </button>
                  {coachSettings?.calendly_url ? (
                    <a href={coachSettings.calendly_url} target="_blank" rel="noreferrer" 
                       data-testid="book-rdv-btn"
                       className="btn-primary flex items-center justify-center gap-2 py-3 no-underline">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Prends un rdv avec ton Major
                    </a>
                  ) : (
                    <button 
                       data-testid="book-rdv-btn" 
                       onClick={() => setActiveTab("messages")}
                       className="btn-secondary flex items-center justify-center gap-2 py-3"
                       title="Contactez votre coach via la messagerie"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                      </svg>
                      Prends un rdv avec ton Major
                    </button>
                  )}
                </div>
              </div>

              {/* AI Summary */}
              <div className="glass-card rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-semibold text-gray-900" style={{ fontFamily: "Outfit, sans-serif" }}>Résumé IA</h4>
                  <button onClick={handleGenerateSummary} disabled={summaryLoading}
                    className="btn-ghost text-xs px-3 py-1.5">
                    {summaryLoading ? "Génération..." : "Générer"}
                  </button>
                </div>
                {summary ? (
                  <p className="text-sm text-gray-700 leading-relaxed">{summary}</p>
                ) : (
                  <p className="text-sm text-gray-500">Cliquez sur "Générer" pour obtenir un résumé de votre progression par l'IA.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Visits Tab */}
      {activeTab === "visits" && (
        <div className="space-y-4 animate-fade-in">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <p className="text-sm text-gray-600">{visits.length} visite{visits.length > 1 ? "s" : ""}</p>
              <div className="flex gap-2 text-xs">
                <span className="px-2 py-1 rounded-full bg-violet-100 text-violet-700">
                  {visits.filter(v => v.source === "odysboard").length} Odysboard
                </span>
                <span className="px-2 py-1 rounded-full bg-cyan-100 text-cyan-700">
                  {visits.filter(v => v.source === "manual").length} manuelles
                </span>
              </div>
            </div>
            <button data-testid="add-visit-btn" onClick={() => setShowVisitModal(true)} className="btn-primary flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              Ajouter
            </button>
          </div>
          {visits.length === 0 ? (
            <div className="glass-card rounded-2xl p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                </svg>
              </div>
              <p className="text-gray-500">Aucune visite enregistrée</p>
              <p className="text-xs text-gray-400 mt-2">Connectez votre Odysboard ou ajoutez manuellement</p>
            </div>
          ) : (
            <div className="space-y-4">
              {visits.map(v => (
                <div key={v.id} className={`glass-card glass-card-hover rounded-2xl p-5 ${v.source === "odysboard" ? "border-l-4 border-l-violet-500" : "border-l-4 border-l-cyan-500"}`} data-testid={`visit-${v.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-[#1a2744] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                        <p className="font-semibold text-gray-900">{v.address}</p>
                        {v.source === "odysboard" ? (
                          <span className="px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-xs font-medium">Odysboard</span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-700 text-xs font-medium">Manuel</span>
                        )}
                        {v.status && (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            v.status === "validee" || v.status === "validée" ? "bg-emerald-100 text-emerald-700" :
                            v.status === "annulee" || v.status === "annulée" ? "bg-red-100 text-red-700" :
                            "bg-amber-100 text-amber-700"
                          }`}>{v.status}</span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-4 mt-2 text-sm ml-7">
                        {v.visit_date && <span className="text-gray-500">{v.visit_date}</span>}
                        {v.ville && <span className="text-gray-500">{v.ville}</span>}
                        {v.surface && <span className="text-gray-500">{v.surface} m²</span>}
                        {v.price && <span className="text-emerald-600 font-semibold">{Number(v.price).toLocaleString("fr-FR")} €</span>}
                        {v.link && <a href={v.link} target="_blank" rel="noreferrer" className="text-[#1a2744] hover:text-[#2d3e5f] underline">Annonce</a>}
                      </div>
                      {v.comments && <p className="text-sm text-gray-500 mt-2 ml-7">{v.comments}</p>}
                    </div>
                    {v.source === "manual" && (
                      <button onClick={() => handleDeleteVisit(v.id)} className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg transition-colors">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </button>
                    )}
                  </div>
                  {v.source === "manual" && <PhotoGallery visitId={v.id} photos={v.photos || []} onUpdate={fetchAll} />}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Cahier Tab */}
      {activeTab === "cahier" && (
        <div className="space-y-4 animate-fade-in">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900" style={{ fontFamily: "Outfit, sans-serif" }}>Cahier des charges</h3>
            <button onClick={() => setCahierEditing(!cahierEditing)} className={cahierEditing ? "btn-ghost" : "btn-primary"}>
              {cahierEditing ? "Annuler" : "Modifier"}
            </button>
          </div>
          <div className="glass-card rounded-2xl p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { key: "ville", label: "Ville", type: "text" },
                { key: "quartier", label: "Quartier", type: "text" },
                { key: "budget_total", label: "Budget total (€)", type: "number" },
                { key: "type_bien", label: "Type de bien", type: "select", options: ["Appartement", "Maison", "Immeuble", "Local commercial"] },
                { key: "surface", label: "Surface (m²)", type: "number" },
                { key: "nb_pieces", label: "Nb pièces", type: "number" },
                { key: "niveau_travaux", label: "Niveau de travaux", type: "select", options: ["Aucun", "Rafraîchissement", "Rénovation partielle", "Rénovation totale"] },
                { key: "ascenseur", label: "Ascenseur", type: "select", options: ["oui", "non", "indifferent"] },
                { key: "chauffage", label: "Chauffage", type: "select", options: ["Individuel", "Collectif", "Indifférent"] },
                { key: "situation_juridique", label: "Situation juridique", type: "select", options: ["Libre", "Occupé", "Indifférent"] },
                { key: "rendement", label: "Rendement min. (%)", type: "number" },
              ].map(field => (
                <div key={field.key}>
                  <label className="text-xs font-medium text-gray-500 block mb-2">{field.label}</label>
                  {cahierEditing ? (
                    field.type === "select" ? (
                      <select data-testid={`cahier-${field.key}`} className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 text-sm focus:outline-none focus:border-[#1a2744]"
                        value={cahierForm[field.key] || ""}
                        onChange={e => setCahierForm({ ...cahierForm, [field.key]: e.target.value })}>
                        <option value="">Sélectionner</option>
                        {field.options.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input data-testid={`cahier-${field.key}`} type={field.type} className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 text-sm focus:outline-none focus:border-[#1a2744]"
                        value={cahierForm[field.key] || ""}
                        onChange={e => setCahierForm({ ...cahierForm, [field.key]: e.target.value })} />
                    )
                  ) : (
                    <p className="text-sm font-medium text-gray-900">{cahier[field.key] ? String(cahier[field.key]) : "-"}</p>
                  )}
                </div>
              ))}
            </div>
            {cahierEditing && (
              <>
                <div className="mt-4">
                  <label className="text-xs font-medium text-gray-500 block mb-2">Préférences</label>
                  <textarea rows={3} className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 text-sm resize-none focus:outline-none focus:border-[#1a2744]" value={cahierForm.preferences || ""} onChange={e => setCahierForm({ ...cahierForm, preferences: e.target.value })} />
                </div>
                <div className="mt-3">
                  <label className="text-xs font-medium text-gray-500 block mb-2">Contraintes</label>
                  <textarea rows={3} className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 text-sm resize-none focus:outline-none focus:border-[#1a2744]" value={cahierForm.contraintes || ""} onChange={e => setCahierForm({ ...cahierForm, contraintes: e.target.value })} />
                </div>
                <button data-testid="save-cahier-btn" onClick={handleSaveCahier} className="btn-primary mt-4">Sauvegarder</button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Notes Tab */}
      {activeTab === "notes" && (
        <div className="space-y-4 animate-fade-in">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900" style={{ fontFamily: "Outfit, sans-serif" }}>Mes notes personnelles</h3>
            {notesEditing ? (
              <div className="flex gap-2">
                <button onClick={() => setNotesEditing(false)} className="btn-ghost">Annuler</button>
                <button data-testid="save-notes-btn" onClick={handleSaveNotes} className="btn-primary">Sauvegarder</button>
              </div>
            ) : (
              <button onClick={() => setNotesEditing(true)} className="btn-ghost">Modifier</button>
            )}
          </div>
          <div className="glass-card rounded-2xl p-6">
            {notesEditing ? (
              <textarea data-testid="notes-textarea" rows={15}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 text-sm resize-none focus:outline-none focus:border-[#1a2744]"
                placeholder="Écrivez vos notes personnelles ici..."
                value={notes}
                onChange={e => setNotes(e.target.value)} />
            ) : (
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap min-h-[200px]">
                {notes || <span className="text-gray-500">Aucune note. Cliquez sur "Modifier" pour commencer.</span>}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Parcours Tab */}
      {activeTab === "parcours" && (
        <div className="animate-fade-in space-y-4 sm:space-y-6">
          <InvestmentObjectiveCard studentId={user?.id} />
          <ParcoursView studentId={user?.id} isCoach={false} />
        </div>
      )}

      {/* Badges Tab */}
      {activeTab === "badges" && (
        <div className="animate-fade-in">
          <BadgesView studentId={user?.id} />
        </div>
      )}

      {/* Sessions Tab */}
      {activeTab === "sessions" && (
        <div className="animate-fade-in">
          <CoachingSessionsView />
        </div>
      )}

      {/* Odysboard Tab */}
      {activeTab === "odysboard" && <OdysboardView />}

      {activeTab === "agenda" && (
        <div className="animate-fade-in" data-testid="student-agenda-tab">
          <AgendaView />
        </div>
      )}

      {activeTab === "notifications" && (
        <div className="animate-fade-in max-w-2xl" data-testid="student-notifications-tab">
          <NotificationPreferences />
        </div>
      )}

      {/* Visit Modal */}
      {showVisitModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl animate-fade-in-up">
            <h3 className="text-lg font-bold text-gray-900 mb-5" style={{ fontFamily: "Outfit, sans-serif" }}>Nouvelle visite</h3>
            <div className="space-y-3">
              <input data-testid="visit-address-input" type="text" placeholder="Adresse du bien*" className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 text-sm focus:outline-none focus:border-[#1a2744]" value={visitForm.address} onChange={e => setVisitForm({ ...visitForm, address: e.target.value })} />
              <input data-testid="visit-date-input" type="date" className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 text-sm focus:outline-none focus:border-[#1a2744]" value={visitForm.visit_date} onChange={e => setVisitForm({ ...visitForm, visit_date: e.target.value })} />
              <input type="number" placeholder="Prix (€)" className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 text-sm focus:outline-none focus:border-[#1a2744]" value={visitForm.price} onChange={e => setVisitForm({ ...visitForm, price: e.target.value })} />
              <input type="url" placeholder="Lien annonce (optionnel)" className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 text-sm focus:outline-none focus:border-[#1a2744]" value={visitForm.link} onChange={e => setVisitForm({ ...visitForm, link: e.target.value })} />
              <textarea rows={3} placeholder="Commentaires" className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 text-sm resize-none focus:outline-none focus:border-[#1a2744]" value={visitForm.comments} onChange={e => setVisitForm({ ...visitForm, comments: e.target.value })} />
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowVisitModal(false)} className="btn-ghost flex-1">Annuler</button>
              <button data-testid="save-visit-btn" onClick={handleCreateVisit} disabled={!visitForm.address || !visitForm.visit_date} className="btn-primary flex-1 disabled:opacity-50">Ajouter</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
